<template>
    <main class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-y-auto">
        <form @submit.prevent="saveCompany">
            <div class="px-8 sm:px-8 lg:px-8 pt-6 pb-2">
                <div class="w-full xl:w-3/4 mx-auto flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <h1 class="stage-page-title">Unternehmen anlegen</h1>
                    <div class="inline-flex">
                        <router-link class="btn-stage-secondary mr-3" to="/workspace/companies">
                            {{ $t('Back') }}
                        </router-link>
                        <button class="btn-stage-primary" type="submit">
                            Speichern
                        </button>
                    </div>
                </div>
            </div>
            <div class="stage-page-body">
                <div class="w-full xl:w-3/4 mx-auto space-y-6">
                    <loading :status="loading.form"/>
                    <div class="space-y-6">
                        <section class="overflow-hidden bg-white px-6 pb-6 shadow sm:rounded">
                            <h3 class="-mx-6 flex h-16 items-center border-b border-ultralight stage-header-surface px-6 text-base font-semibold leading-6 text-gray-900">
                                Unternehmen
                            </h3>
                            <div class="mt-5 grid grid-cols-1 gap-x-5 gap-y-4 md:grid-cols-6">
                                <div class="md:col-span-2">
                                    <label class="stage-filter-label block mb-2" for="company-type">Typ</label>
                                    <select
                                        id="company-type"
                                        v-model="company.company_type"
                                        class="stage-filter-input form-select block w-full text-sm sm:leading-5"
                                        required
                                    >
                                        <option value="company">Unternehmen</option>
                                        <option value="institution">Institution</option>
                                        <option value="association">Verein</option>
                                        <option value="law_firm">Kanzlei</option>
                                    </select>
                                </div>
                                <div class="md:col-span-4">
                                    <label class="stage-filter-label block mb-2" for="company-name">Name</label>
                                    <input
                                        id="company-name"
                                        v-model="company.name"
                                        class="stage-filter-input form-input block w-full text-sm sm:leading-5"
                                        maxlength="255"
                                        placeholder="Unternehmensname"
                                        required
                                    >
                                </div>
                                <div class="md:col-span-6">
                                    <label class="stage-filter-label block mb-2">Unternehmensbild</label>
                                    <div class="flex items-center select-none">
                                        <div class="avatar-editor relative inline-block h-12 w-12 rounded-sm overflow-hidden bg-gray-100">
                                            <button
                                                v-show="hasUploadedImage"
                                                class="avatar-editor-remove absolute top-0 right-0 items-center p-0.5 rounded-full leading-4 bg-red-600 text-white cursor-pointer"
                                                type="button"
                                                @click="removeImage"
                                            >
                                                <svg-vue class="h-4 w-4 p-px" icon="font-awesome.times-light"></svg-vue>
                                            </button>
                                            <img
                                                :src="imagePreviewUrl"
                                                :alt="company.name || 'Unternehmensbild'"
                                                class="h-full w-full text-gray-300 rounded-sm object-cover"
                                            />
                                        </div>
                                        <span class="ml-5">
                                            <input ref="changeImage" accept=".png,.jpg,.jpeg" hidden type="file" @change="changeImage($event)">
                                            <button
                                                class="py-2 px-3 border border-bordergray-100 rounded text-sm leading-4 font-medium text-gray-700 bg-white hover:text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-0"
                                                type="button"
                                                @click="selectImage"
                                            >
                                                Auswählen
                                            </button>
                                        </span>
                                    </div>
                                    <p class="mt-2 text-xs leading-4 text-gray-500">
                                        Quadratisches PNG oder JPEG, maximal 1000 × 1000 Pixel.
                                    </p>
                                </div>
                            </div>
                        </section>

                        <section class="overflow-hidden bg-white px-6 pb-6 shadow sm:rounded">
                            <h3 class="-mx-6 flex h-16 items-center border-b border-ultralight stage-header-surface px-6 text-base font-semibold leading-6 text-gray-900">
                                Anschrift
                            </h3>
                            <div class="mt-5 grid grid-cols-1 gap-x-5 gap-y-4 md:grid-cols-6">
                                <div class="md:col-span-3">
                                    <label class="stage-filter-label block mb-2" for="company-street">Straße</label>
                                    <input id="company-street" v-model.trim="company.street" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="255" required>
                                </div>
                                <div class="md:col-span-1">
                                    <label class="stage-filter-label block mb-2" for="company-house-number">Hausnummer</label>
                                    <input id="company-house-number" v-model.trim="company.house_number" placeholder="Nr" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="32" required>
                                </div>
                                <div class="md:col-span-2">
                                    <label class="stage-filter-label block mb-2" for="company-postal-code">PLZ</label>
                                    <input id="company-postal-code" v-model.trim="company.postal_code" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="20" pattern="[A-Za-z0-9][A-Za-z0-9 \-]{1,19}" required title="Bitte geben Sie eine gültige Postleitzahl ein.">
                                </div>
                                <div class="md:col-span-3">
                                    <label class="stage-filter-label block mb-2" for="company-city">Ort</label>
                                    <input id="company-city" v-model.trim="company.city" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="120" required>
                                </div>
                                <div class="md:col-span-3">
                                    <label class="stage-filter-label block mb-2" for="company-country">Land</label>
                                    <input id="company-country" v-model.trim="company.country" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="120" required>
                                </div>
                            </div>
                        </section>

                        <section class="overflow-hidden bg-white px-6 pb-6 shadow sm:rounded">
                            <h3 class="-mx-6 flex h-16 items-center border-b border-ultralight stage-header-surface px-6 text-base font-semibold leading-6 text-gray-900">
                                Kontakt
                            </h3>
                            <div class="mt-5 grid grid-cols-1 gap-x-5 gap-y-4 md:grid-cols-2">
                                <div>
                                    <label class="stage-filter-label block mb-2" for="company-email">E-Mail (Zentrale)</label>
                                    <input id="company-email" v-model.trim="company.email" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="255" required type="email">
                                </div>
                                <div>
                                    <label class="stage-filter-label block mb-2" for="company-phone">Telefon (Zentrale)</label>
                                    <input id="company-phone" v-model.trim="company.phone" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="64" pattern="\+?[0-9][0-9 ()/.\-]{2,63}" required title="Bitte geben Sie eine gültige Telefonnummer ein." type="tel">
                                </div>
                                <div class="md:col-span-2">
                                    <label class="stage-filter-label block mb-2" for="company-website">Website</label>
                                    <div class="relative flex rounded">
                                        <span class="inline-flex items-center px-3 rounded-l border border-r-0 border-bordergray-100 bg-gray-50 text-gray-500 text-sm">https://</span>
                                        <input
                                            id="company-website"
                                            v-model="company.website"
                                            class="stage-filter-input form-input block w-full rounded-l-none text-sm sm:leading-5"
                                            inputmode="url"
                                            maxlength="255"
                                            placeholder="example.com"
                                        >
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section class="overflow-hidden bg-white px-6 pb-6 shadow sm:rounded">
                            <h3 class="-mx-6 flex h-16 items-center border-b border-ultralight stage-header-surface px-6 text-base font-semibold leading-6 text-gray-900">
                                Social Media
                            </h3>
                            <div class="mt-5 grid grid-cols-1 gap-x-5 gap-y-4 md:grid-cols-2">
                                <div>
                                    <label class="stage-filter-label block mb-2" for="company-linkedin">LinkedIn</label>
                                    <input id="company-linkedin" v-model.trim="company.linkedin_url" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="255" placeholder="https://www.linkedin.com/company/..." type="url">
                                </div>
                                <div>
                                    <label class="stage-filter-label block mb-2" for="company-xing">XING</label>
                                    <input id="company-xing" v-model.trim="company.xing_url" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="255" placeholder="https://www.xing.com/pages/..." type="url">
                                </div>
                                <div>
                                    <label class="stage-filter-label block mb-2" for="company-instagram">Instagram</label>
                                    <input id="company-instagram" v-model.trim="company.instagram_url" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="255" placeholder="https://www.instagram.com/..." type="url">
                                </div>
                                <div>
                                    <label class="stage-filter-label block mb-2" for="company-facebook">Facebook</label>
                                    <input id="company-facebook" v-model.trim="company.facebook_url" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="255" placeholder="https://www.facebook.com/..." type="url">
                                </div>
                            </div>
                        </section>

                        <section class="overflow-hidden bg-white px-6 pb-6 shadow sm:rounded">
                            <h3 class="-mx-6 flex h-16 items-center border-b border-ultralight stage-header-surface px-6 text-base font-semibold leading-6 text-gray-900">
                                Steuerdaten
                            </h3>
                            <div class="mt-5 grid grid-cols-1 gap-x-5 gap-y-4 md:grid-cols-2">
                                <div>
                                    <label class="stage-filter-label block mb-2" for="company-tax-number">Steuernummer</label>
                                    <input id="company-tax-number" v-model="company.tax_number" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="120" pattern="[A-Za-z0-9][A-Za-z0-9 ./\-]*" title="Bitte geben Sie eine gültige Steuernummer ein.">
                                </div>
                                <div>
                                    <label class="stage-filter-label block mb-2" for="company-vat-id">USt-ID</label>
                                    <input id="company-vat-id" v-model="company.vat_id" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="23" pattern="[A-Za-z]{2}[A-Za-z0-9 .\-]{2,20}" placeholder="DE123456789" title="Bitte geben Sie eine gültige USt-ID ein.">
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
                <div class="w-full xl:w-3/4 mx-auto bg-white shadow sm:rounded overflow-hidden mt-6">
                    <div class="p-6 md:grid md:grid-cols-3 md:gap-x-6">
                        <div class="-mx-6 -mt-6 mb-6 flex h-16 items-center border-b border-ultralight stage-header-surface px-6 md:col-span-3">
                            <h3 class="text-base font-semibold leading-6 text-gray-900">Unternehmensrolle</h3>
                        </div>
                        <div class="md:col-span-3">
                            <p class="mb-4 text-sm text-gray-500">
                                Die Unternehmensrolle wird einmalig bei der Anlage festgelegt und kann später nicht mehr geändert werden.
                            </p>
                            <div class="form-selection-panel overflow-hidden">
                                <label class="form-selection-option flex items-center px-3 py-3 cursor-pointer">
                                    <input
                                        v-model="billingRole"
                                        class="form-radio h-4 w-4 text-gray-700 mr-3"
                                        name="billing-role-new"
                                        required
                                        type="radio"
                                        value="customer"
                                    >
                                    <div>
                                        <div class="text-sm font-medium text-gray-900">Kunde</div>
                                        <div class="mt-0.5 text-xs text-gray-500">
                                            Für Unternehmen, die Leistungen beziehen und als Auftraggeber abgerechnet werden.
                                        </div>
                                    </div>
                                </label>
                                <label class="form-selection-option flex items-center px-3 py-3 cursor-pointer">
                                    <input
                                        v-model="billingRole"
                                        class="form-radio h-4 w-4 text-gray-700 mr-3"
                                        name="billing-role-new"
                                        required
                                        type="radio"
                                        value="provider"
                                    >
                                    <div>
                                        <div class="text-sm font-medium text-gray-900">Dienstleister</div>
                                        <div class="mt-0.5 text-xs text-gray-500">
                                            Für Unternehmen, die Leistungen erbringen und als Auftragnehmer abgerechnet werden.
                                        </div>
                                    </div>
                                </label>
                                <label class="form-selection-option flex items-center px-3 py-3 cursor-pointer">
                                    <input
                                        v-model="billingRole"
                                        class="form-radio h-4 w-4 text-gray-700 mr-3"
                                        name="billing-role-new"
                                        required
                                        type="radio"
                                        value="contact"
                                    >
                                    <div>
                                        <div class="text-sm font-medium text-gray-900">Externer Kontakt</div>
                                        <div class="mt-0.5 text-xs text-gray-500">
                                            Für Unternehmen ohne Abrechnungsrolle, die ausschließlich zu Kontaktzwecken geführt werden.
                                        </div>
                                    </div>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    v-if="company.is_customer_company"
                    class="mt-6 w-full overflow-hidden bg-white shadow sm:rounded xl:mx-auto xl:w-1/2"
                >
                    <div class="flex h-16 items-center border-b border-ultralight stage-header-surface px-6">
                        <h3 class="text-base font-semibold leading-6 text-gray-900">Abrechnung (Betreiber an Kunde)</h3>
                    </div>
                    <div class="grid grid-cols-1 gap-6 p-6 md:grid-cols-2">
                        <div>
                            <label class="block text-sm font-medium leading-5 text-gray-700" for="company-customer-hourly-rate">Stundensatz (Netto) (€)</label>
                            <div class="mt-1 relative rounded shadow-sm">
                                <input
                                    id="company-customer-hourly-rate"
                                    v-model="company.customer_hourly_rate"
                                    class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                    min="0"
                                    placeholder="0.00"
                                    step="0.01"
                                    type="number"
                                >
                            </div>
                        </div>
                        <div>
                            <label class="block text-sm font-medium leading-5 text-gray-700" for="company-customer-vat-rate">Umsatzsteuer (%)</label>
                            <div class="mt-1 relative rounded shadow-sm">
                                <input
                                    id="company-customer-vat-rate"
                                    v-model="company.customer_vat_rate"
                                    class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                    max="100"
                                    min="0"
                                    placeholder="19"
                                    step="0.01"
                                    type="number"
                                >
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    v-if="company.is_provider_company"
                    class="mt-6 w-full overflow-hidden bg-white shadow sm:rounded xl:mx-auto xl:w-1/2"
                >
                    <div class="flex h-16 items-center border-b border-ultralight stage-header-surface px-6">
                        <h3 class="text-base font-semibold leading-6 text-gray-900">Abrechnung (Dienstleister an Betreiber)</h3>
                    </div>
                    <div class="grid grid-cols-1 gap-6 p-6 md:grid-cols-2">
                        <div>
                            <label class="block text-sm font-medium leading-5 text-gray-700" for="company-provider-hourly-rate">Stundensatz (Netto) (€)</label>
                            <div class="mt-1 relative rounded shadow-sm">
                                <input
                                    id="company-provider-hourly-rate"
                                    v-model="company.provider_hourly_rate"
                                    class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                    min="0"
                                    placeholder="0.00"
                                    step="0.01"
                                    type="number"
                                >
                            </div>
                        </div>
                        <div>
                            <label class="block text-sm font-medium leading-5 text-gray-700" for="company-provider-vat-rate">Umsatzsteuer (%)</label>
                            <div class="mt-1 relative rounded shadow-sm">
                                <input
                                    id="company-provider-vat-rate"
                                    v-model="company.provider_vat_rate"
                                    class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                    max="100"
                                    min="0"
                                    placeholder="19"
                                    step="0.01"
                                    type="number"
                                >
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    v-if="company.is_contact_company"
                    class="mt-6 w-full overflow-hidden bg-white shadow sm:rounded xl:mx-auto xl:w-1/2"
                >
                    <div class="flex h-16 items-center border-b border-ultralight stage-header-surface px-6">
                        <h3 class="text-base font-semibold leading-6 text-gray-900">Sichtbarkeit im Bereich Beziehungen</h3>
                    </div>
                    <div class="p-6">
                        <p class="text-sm text-gray-500">
                            Die Freigaben bestimmen, welche Benutzergruppen dieses Unternehmen im Bereich Beziehungen sehen können.
                        </p>
                        <h4 class="mt-5 text-sm font-semibold text-gray-900">Benutzergruppen</h4>
                        <p class="mt-1 text-sm text-gray-500">
                            Mitarbeiter und Mitarbeitende von Dienstleistern sehen dieses Unternehmen mit der Rolle „Externer Kontakt“ nur nach ausdrücklicher Freigabe.
                        </p>
                        <div class="form-selection-panel mt-4 overflow-hidden">
                            <label class="form-selection-option flex cursor-pointer items-center px-3 py-3 text-sm text-gray-700">
                                <input v-model="company.relationships_visible_to_employees" class="form-checkbox mr-3 h-4 w-4 text-gray-700" type="checkbox">
                                Mitarbeiter
                            </label>
                            <label class="form-selection-option flex cursor-pointer items-center px-3 py-3 text-sm text-gray-700">
                                <input v-model="company.relationships_visible_to_service_providers" class="form-checkbox mr-3 h-4 w-4 text-gray-700" type="checkbox">
                                Mitarbeitende Dienstleister
                            </label>
                        </div>
                        <h4 class="mt-6 text-sm font-semibold text-gray-900">Für Kundenunternehmen sichtbar</h4>
                        <p class="mt-1 text-sm text-gray-500">
                            Kunden sehen dieses Unternehmen mit der Rolle „Externer Kontakt“ ausschließlich, wenn ihr eigenes Unternehmen hier ausdrücklich ausgewählt wurde.
                        </p>
                        <div class="form-selection-panel mt-3 max-h-56 overflow-y-auto">
                            <label
                                v-for="customerCompany in customerCompanyOptions"
                                :key="'contact-company-customer-' + customerCompany.id"
                                class="form-selection-option flex cursor-pointer items-center px-3 py-2"
                            >
                                <input
                                    v-model="company.relationship_customer_company_ids"
                                    :value="Number(customerCompany.id)"
                                    class="form-checkbox mr-3 h-4 w-4 flex-shrink-0 text-gray-700"
                                    type="checkbox"
                                >
                                <img
                                    :alt="customerCompany.name"
                                    :src="customerCompany.image"
                                    class="mr-3 h-6 w-6 flex-shrink-0 rounded-sm object-cover"
                                >
                                <span class="truncate text-sm text-gray-700">{{ customerCompany.name }}</span>
                            </label>
                            <div v-if="customerCompanyOptions.length < 1" class="px-3 py-2 text-sm text-gray-500">
                                Keine Kundenunternehmen verfügbar.
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    </main>
</template>

<script>
export default {
    name: 'dashboard-companies-new',
    metaInfo() {
        return {
            title: 'Unternehmen anlegen',
        };
    },
    data() {
        return {
            loading: {
                form: false,
            },
            customerCompanyOptions: [],
            company: {
                company_type: 'company',
                name: '',
                website: '',
                email: '',
                phone: '',
                linkedin_url: '',
                xing_url: '',
                instagram_url: '',
                facebook_url: '',
                tax_number: '',
                vat_id: '',
                country: '',
                city: '',
                postal_code: '',
                street: '',
                house_number: '',
                is_customer_company: false,
                is_provider_company: false,
                is_contact_company: false,
                relationships_visible_to_customers: false,
                relationships_visible_to_employees: false,
                relationships_visible_to_service_providers: false,
                relationship_customer_company_ids: [],
                customer_hourly_rate: null,
                customer_vat_rate: null,
                provider_hourly_rate: null,
                provider_vat_rate: null,
                image: null,
                image_preview: null,
            },
        };
    },
    computed: {
        billingRole: {
            get() {
                if (this.company.is_customer_company) {
                    return 'customer';
                }

                if (this.company.is_provider_company) {
                    return 'provider';
                }

                if (this.company.is_contact_company) {
                    return 'contact';
                }

                return null;
            },
            set(value) {
                this.company.is_customer_company = value === 'customer';
                this.company.is_provider_company = value === 'provider';
                this.company.is_contact_company = value === 'contact';
                if (value === 'customer') {
                    this.company.provider_hourly_rate = null;
                    this.company.provider_vat_rate = null;
                }
                if (value === 'provider') {
                    this.company.customer_hourly_rate = null;
                    this.company.customer_vat_rate = null;
                }
                if (value !== 'contact') {
                    this.company.relationships_visible_to_customers = false;
                    this.company.relationships_visible_to_employees = false;
                    this.company.relationships_visible_to_service_providers = false;
                    this.company.relationship_customer_company_ids = [];
                }
                if (value === 'contact') {
                    this.company.customer_hourly_rate = null;
                    this.company.customer_vat_rate = null;
                    this.company.provider_hourly_rate = null;
                    this.company.provider_vat_rate = null;
                }
            },
        },
        hasUploadedImage() {
            if (typeof File === 'undefined') {
                return !!this.company.image;
            }

            return this.company.image instanceof File;
        },
        imagePreviewUrl() {
            if (this.company.image_preview) {
                return this.company.image_preview;
            }

            return this.generateFallbackImage();
        },
    },
    mounted() {
        this.loadCustomerCompanyOptions();
    },
    methods: {
        loadCustomerCompanyOptions() {
            axios.get('api/workspace/admin/companies').then((response) => {
                const items = response.data && Array.isArray(response.data.items) ? response.data.items : [];
                this.customerCompanyOptions = items
                    .filter((company) => !!company.is_customer_company)
                    .map((company) => ({
                        id: Number(company.id),
                        name: company.name,
                        image: company.image || '/images/default/icon.png',
                    }));
            }).catch(() => {
                this.customerCompanyOptions = [];
            });
        },
        saveCompany() {
            const self = this;
            self.loading.form = true;

            const formData = new FormData();
            formData.append('name', self.company.name || '');
            formData.append('company_type', self.company.company_type || 'company');
            formData.append('website', self.normalizeWebsite(self.company.website) || '');
            formData.append('email', self.company.email || '');
            formData.append('phone', self.company.phone || '');
            formData.append('linkedin_url', self.company.linkedin_url || '');
            formData.append('xing_url', self.company.xing_url || '');
            formData.append('instagram_url', self.company.instagram_url || '');
            formData.append('facebook_url', self.company.facebook_url || '');
            formData.append('tax_number', self.company.tax_number || '');
            formData.append('vat_id', self.company.vat_id || '');
            formData.append('country', self.company.country || '');
            formData.append('city', self.company.city || '');
            formData.append('postal_code', self.company.postal_code || '');
            formData.append('street', self.company.street || '');
            formData.append('house_number', self.company.house_number || '');
            formData.append('is_customer_company', self.company.is_customer_company ? '1' : '0');
            formData.append('is_provider_company', self.company.is_provider_company ? '1' : '0');
            formData.append('is_contact_company', self.company.is_contact_company ? '1' : '0');
            formData.append('relationships_visible_to_customers', self.company.relationships_visible_to_customers ? '1' : '0');
            formData.append('relationships_visible_to_employees', self.company.relationships_visible_to_employees ? '1' : '0');
            formData.append('relationships_visible_to_service_providers', self.company.relationships_visible_to_service_providers ? '1' : '0');
            self.company.relationship_customer_company_ids.forEach(function (companyId, index) {
                formData.append(`relationship_customer_company_ids[${index}]`, String(Number(companyId)));
            });
            formData.append('customer_hourly_rate', self.normalizeDecimalValue(self.company.customer_hourly_rate) || '');
            formData.append('customer_vat_rate', self.normalizeDecimalValue(self.company.customer_vat_rate) || '');
            formData.append('provider_hourly_rate', self.normalizeDecimalValue(self.company.provider_hourly_rate) || '');
            formData.append('provider_vat_rate', self.normalizeDecimalValue(self.company.provider_vat_rate) || '');
            if (self.company.image instanceof File) {
                formData.append('image', self.company.image);
            }

            axios.post('api/workspace/admin/companies', formData, {
                headers: {'Content-Type': 'multipart/form-data'},
            }).then(function (response) {
                self.loading.form = false;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data saved correctly').toString(),
                    type: 'success',
                });
                const createdId = response.data && response.data.company ? response.data.company.id : null;
                if (createdId) {
                    self.$router.push('/workspace/companies/' + createdId + '/edit');
                    return;
                }

                self.$router.push('/workspace/companies');
            }).catch(function () {
                self.loading.form = false;
            });
        },
        selectImage() {
            this.$refs.changeImage.click();
        },
        removeImage() {
            this.company.image = null;
            this.company.image_preview = null;
            if (this.$refs.changeImage) {
                this.$refs.changeImage.value = '';
            }
        },
        changeImage(event) {
            const self = this;
            if (!event.target.files.length) {
                return;
            }

            const file = event.target.files[0];
            if (['image/png', 'image/x-citrix-png', 'image/x-png', 'image/jpeg', 'image/x-citrix-jpeg'].includes(file.type)) {
                self.company.image = file;
                self.company.image_preview = URL.createObjectURL(file);
                return;
            }

            self.$notify({
                title: self.$i18n.t('Unsupported file type').toString(),
                text: self.$i18n.t('Only image type files are accepted').toString(),
                type: 'error',
            });
            event.target.value = '';
        },
        normalizeWebsite(value) {
            if (!value) {
                return null;
            }

            return String(value).replace(/^https?:\/\//i, '').trim();
        },
        normalizeDecimalValue(value) {
            if (value === null || typeof value === 'undefined' || value === '') {
                return null;
            }

            const normalized = String(value).replace(',', '.').trim();
            if (normalized === '') {
                return null;
            }

            const parsed = Number(normalized);
            if (!Number.isFinite(parsed)) {
                return null;
            }

            return parsed.toFixed(2);
        },
        generateFallbackImage() {
            const seedText = String(this.company.name || 'C').trim() || 'C';
            const firstChar = seedText.charAt(0).toUpperCase();
            const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80" width="80" height="80"><defs><linearGradient id="companyGradientNewDashboard" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="hsl(210 35% 48%)"/><stop offset="100%" stop-color="hsl(210 35% 34%)"/></linearGradient></defs><rect width="80" height="80" fill="url(#companyGradientNewDashboard)"/><text x="50%" y="54%" text-anchor="middle" dominant-baseline="middle" fill="rgba(255,255,255,0.55)" font-family="Arial, sans-serif" font-size="34" font-weight="700">${firstChar}</text></svg>`;
            return 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)));
        },
    },
};
</script>

<style lang="scss">
@reference "../../../../../sass/tailwind-reference.css";

.avatar-editor {
    .avatar-editor-remove {
        @apply hidden;
    }

    &:hover {
        .avatar-editor-remove {
            @apply inline-flex;
        }
    }
}
</style>
